import {
	CAREER_OFFER_EVENTS,
	CAREER_OFFER_GOALS,
	type CareerOfferEvent,
	type CareerOfferForm,
	type CareerOfferGoal,
	type CareerOfferTechnology,
} from './events.ts';

type CareerOfferFormAnalyticsAdapter = {
	trackEvent: (event: CareerOfferEvent) => void;
	trackGoal: (goal: CareerOfferGoal, event: CareerOfferEvent) => void;
};

const startedGoals = {
	application: CAREER_OFFER_GOALS.APPLICATION_STARTED,
	question: CAREER_OFFER_GOALS.QUESTION_STARTED,
} as const;

const submittedGoals = {
	application: CAREER_OFFER_GOALS.APPLICATION_SUBMITTED,
	question: CAREER_OFFER_GOALS.QUESTION_SUBMITTED,
} as const;

export const createCareerOfferFormTracker = (
	form: CareerOfferForm,
	technology: CareerOfferTechnology | undefined,
	analytics: CareerOfferFormAnalyticsAdapter,
) => {
	let viewed = false;
	let started = false;
	const context = { ...(technology && { technology }), form };

	return {
		trackView() {
			if (viewed) return;
			viewed = true;
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.FORM_VIEW,
				...context,
			});
		},
		trackStart() {
			if (started) return;
			started = true;
			analytics.trackGoal(startedGoals[form], {
				name: CAREER_OFFER_EVENTS.FORM_START,
				...context,
			});
		},
		trackSubmitAttempt() {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.FORM_SUBMIT_ATTEMPT,
				...context,
			});
		},
		trackValidationError(fields: string[]) {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.FORM_VALIDATION_ERROR,
				...context,
				value: [...fields].sort().join(','),
			});
		},
		trackCaptchaRequested() {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.CAPTCHA_REQUESTED,
				...context,
			});
		},
		trackCaptchaPassed() {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.CAPTCHA_PASSED,
				...context,
			});
		},
		trackCaptchaError(
			result: 'expired' | 'network' | 'javascript' | 'rejected',
		) {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.CAPTCHA_ERROR,
				...context,
				result,
			});
		},
		trackSubmitResult(result: 'created' | 'already-submitted') {
			if (result === 'created') {
				analytics.trackGoal(submittedGoals[form], {
					name: CAREER_OFFER_EVENTS.FORM_SUBMITTED,
					...context,
					result,
				});
				return;
			}
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.FORM_DUPLICATE,
				...context,
				result,
			});
		},
		trackSubmitError(result: 'contract' | 'server' | 'network') {
			analytics.trackEvent({
				name: CAREER_OFFER_EVENTS.FORM_SUBMIT_ERROR,
				...context,
				result,
			});
		},
	};
};
